/**
 * @file   src\components\AgentWantAd\WantAdMustHaves.js
 * @brief  This file is responsible for handling wantad must haves in edit want-ad.
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useRef } from "react";
import { Button, ButtonGroup, FormLabel } from "react-bootstrap";
import {
  handleTextBoxChange,
  handleRadioButonChange,
} from "../../actions/common";
import { checkCharLength } from "../../helpers/validations";
import TextBox from "../Common/TextBox";
import ButtonGroups from "../Common/ButtonGroups";
import SqareFeet from "./v2/SqareFeet";
import WantAdLotSize from "../Common/v2/WantAdLotSize";
import {
  NEW_CONSTRUCTION,
  STORY_ANY,
  STORY_SINGLE,
  YEAR_BUILT_MAX,
  YEAR_BUILT_MIN,
  YEAR_BUILT_RANGE,
} from "../../constants/common";

const WantAdMustHaves = (props) => {
  const {
    handleFormValues,
    noOfBedrooms,
    noOfBathrooms,
    SFMin,
    SFMax,
    garageSpace,
    yearBuiltMin,
    yearBuildMax,
    mustHaveErrors,
    stories,
    yearBuiltOption,
    minLtSz,
    maxLtSz,
    adaAccess,
    requirementBedroomsError,
    requirementBathroomsError,
    propertyType,
  } = props;

  const [yrBuiltMin, setYearBuiltMin] = useState();
  const [yearBuiltMinError, setYearBuiltMinError] = useState("");
  const [yrBuiltMax, setYearBuiltMax] = useState();
  const [yearBuiltMaxError, setYearBuiltMaxError] = useState("");
  const [isDisableYearBuildMin, setMinYearDisable] = useState(false);
  const [isDisableYearBuildMax, setMaxYearDisable] = useState(false);
  const [gSpace, setGSpace] = useState("");

  const yearBuiltMinDateRef = useRef();
  const yearBuiltMaxDateRef = useRef();

  useEffect(() => {
    setYearBuiltMin(yearBuiltMin);
    setYearBuiltMax(yearBuildMax);
    clearGarrageLocalState();
  }, []);

  useEffect(() => {
    if (mustHaveErrors && mustHaveErrors.length > 0) {
      if (
        mustHaveErrors[0] == "yearBuiltMinDate" ||
        mustHaveErrors[0] == "yearBuiltMaxDate"
      ) {
        mustHaveErrors[0] == "yearBuiltMinDate" &&
          yearBuiltMinDateRef.current.focus();
        mustHaveErrors[0] == "yearBuiltMaxDate" &&
          yearBuiltMaxDateRef.current.focus();
      } else if (
        mustHaveErrors[0] === "yearBuiltMaxRange" ||
        mustHaveErrors[0] === "yearBuiltMinRange"
      ) {
        mustHaveErrors[0] == "yearBuiltMaxRange" &&
          yearBuiltMaxDateRef.current.focus();
        mustHaveErrors[0] == "yearBuiltMinRange" &&
          yearBuiltMinDateRef.current.focus();
      } else if (mustHaveErrors[0] == "yearBuiltError") {
        yearBuiltMaxDateRef.current.focus();
      } else if (mustHaveErrors[1] == "yearBuiltError") {
        window.scrollTo(0, yearBuiltMinDateRef.current.offsetTop);
      }

      mustHaveErrors.map((item) => {
        if (item == "yearBuiltMinDate" || item == "yearBuiltMaxDate") {
          item == "yearBuiltMinDate" && setYearBuiltMinError("Enter Min Year");
          item == "yearBuiltMaxDate" && setYearBuiltMaxError("Enter Max Year");
        } else if (
          item === "yearBuiltMaxRange" ||
          item === "yearBuiltMinRange"
        ) {
          item === "yearBuiltMaxRange" &&
            setYearBuiltMaxError(
              `Year should be between ${YEAR_BUILT_MIN} and ${YEAR_BUILT_MAX}`
            );
          item === "yearBuiltMinRange" &&
            setYearBuiltMinError(
              `Year should be between ${YEAR_BUILT_MIN} and ${YEAR_BUILT_MAX}`
            );
        } else if (item == "yearBuiltError") {
          setYearBuiltMaxError(`Value Must be greater than minimum`);
        }
      });
    }
  }, [mustHaveErrors]);

  const clearGarrageLocalState = () => {
    setGSpace("");
  };

  const handleBedRoomClick = (e) => {
    const value = handleRadioButonChange(e);
    handleFormValues({ noBedRm: value, focusOut: true });
    clearGarrageLocalState();
  };

  const handleBathRoomClick = (e) => {
    const value = handleRadioButonChange(e);
    handleFormValues({ noBathRm: value, focusOut: true });
    clearGarrageLocalState();
  };

  const handleGarageClick = (e) => {
    const value = handleRadioButonChange(e);
    setGSpace(value);
    handleFormValues({ grgSpce: value, focusOut: true });
  };

  const handleYearBuiltMinchange = (e) => {
    setYearBuiltMinError("");
    const value = handleTextBoxChange(e);
    if (checkCharLength(value, 4)) {
      setYearBuiltMin(value);
      handleFormValues({ yrBltMin: value });
    }
    clearGarrageLocalState();
  };

  const handleYearBuiltMaxchange = (e) => {
    setYearBuiltMaxError("");
    const value = handleTextBoxChange(e);
    if (checkCharLength(value, 4)) {
      setYearBuiltMax(value);
      handleFormValues({ yrBltMax: value });
    }
    clearGarrageLocalState();
  };

  const handleStorySelection = (selectedStory) => {
    if (selectedStory == stories) {
      handleFormValues({ stories: "", focusOut: true });
    } else {
      handleFormValues({ stories: selectedStory, focusOut: true });
    }
  };

  const handleYearBuiltSelection = (selectedValue) => {
    setYearBuiltMinError("");
    setYearBuiltMaxError("");
    if (selectedValue === yearBuiltOption) {
      handleFormValues({
        yearBuiltOption: "",
        yearBuiltMin: "",
        yearBuildMax: "",
        focusOut: true,
      });
    } else {
      handleFormValues({
        yearBuiltOption: selectedValue,
        yearBuiltMin: "",
        yearBuildMax: "",
        focusOut: true,
      });
    }
  };

  const handleCheckBoxItemClick = (e) => {
    handleFormValues({ adaAccess: e.target.checked });
  };

  return (
    <>
      <div
        className={
          requirementBedroomsError
            ? "btn-group-wrap select-type-wrap red-marked-btngroup"
            : "btn-group-wrap select-type-wrap select-type-btn"
        }
      >
        <FormLabel
          className={requirementBedroomsError ? "red-marked-formlabel" : "wanted-info-label"}
        >
          Bedrooms<span>*</span>
        </FormLabel>
        <ButtonGroups
          variant="outline-primary"
          value={noOfBedrooms}
          items={["Any", "1+", "2+", "3+", "4+", "5+"]}
          onClick={(e) => handleBedRoomClick(e)}
        />
      </div>
      <div
        className={
          requirementBathroomsError
            ? "btn-group-wrap select-type-wrap red-marked-btngroup"
            : "btn-group-wrap select-type-wrap select-type-btn"
        }
      >
        <FormLabel
          className={requirementBathroomsError ? "red-marked-formlabel" : "wanted-info-label"}
        >
          Bathrooms (min) <span>*</span>
        </FormLabel>

        <ButtonGroups
          variant="outline-primary"
          value={noOfBathrooms}
          items={["Any", "1+", "2+", "3+", "4+", "5+"]}
          onClick={(e) => handleBathRoomClick(e)}
        />
      </div>

      <SqareFeet
        handleFormValues={handleFormValues}
        SFMax={SFMax}
        SFMin={SFMin}
        errors={mustHaveErrors}
      />

      <div className="btn-group-wrap select-type-wrap wanted-info-label select-type-btn">
        <FormLabel className="create-want-label">Garage Spaces</FormLabel>

        <ButtonGroups
          variant="outline-primary"
          section="Garage"
          value={garageSpace}
          currentSelectedValue={gSpace}
          items={["Any", "1+", "2+", "3+"]}
          onClick={(e) => handleGarageClick(e)}
          handleFormValues={handleFormValues}
        />
      </div>

      <div className="btn-group-btm-wrap mb-24 wanted-info-label select-type-btn">
        <FormLabel className="create-want-label">Stories</FormLabel>
        <ButtonGroup aria-label="Basic example">
          <Button
            className="w-50"
            value={stories}
            onClick={() => handleStorySelection(STORY_ANY)}
            variant={
              stories === STORY_ANY
                ? "outline-primary active"
                : "outline-primary"
            }
          >
            Any
          </Button>
          <Button
            className="w-50"
            value={stories}
            onClick={() => handleStorySelection(STORY_SINGLE)}
            variant={
              stories === STORY_SINGLE
                ? "outline-primary active"
                : "outline-primary"
            }
          >
            Single Story Only
          </Button>
        </ButtonGroup>
      </div>

      <div className="wantad-timing-wrap pb-0">
        <WantAdLotSize
          handleFormValues={handleFormValues}
          mnLotSze={minLtSz}
          mxLotSze={maxLtSz}
          lotSizeError={mustHaveErrors}
          isFromLongForm={true}
          propertyType={propertyType}
        />
      </div>

      <div className="range-values-wrap">
        <label className="form-label mb-12 wanted-info-label">Year Built</label>
        <div className="row">
          <div className="wantad-timing-wrap pb-2">
            <div className="col-md-12">
              <label className="radio-wrap mb-3">
                <input
                  type="radio"
                  name="radio_yearBuilt"
                  value=""
                  onClick={(e) => handleYearBuiltSelection(YEAR_BUILT_RANGE)}
                  checked={
                    yearBuiltOption && yearBuiltOption == YEAR_BUILT_RANGE
                  }
                  onChange={() => {}} //Avoid console warning
                />
                <span>
                  <div className="timing-wrap-info">
                    <h2 className="wanted-info-list">Set range</h2>
                  </div>
                </span>
              </label>
            </div>
            {yearBuiltOption === YEAR_BUILT_RANGE && (
              <div className="square-footage-wrap mb-1">
                <ul className="range-values-list">
                  <li>
                    <TextBox
                      type="number"
                      minValue={YEAR_BUILT_MIN}
                      maxValue={YEAR_BUILT_MAX}
                      errorMessage={yearBuiltMinError}
                      value={yrBuiltMin}
                      placeholder="Min year"
                      disabled={isDisableYearBuildMin}
                      className="form-control"
                      onChange={(e) => {
                        handleYearBuiltMinchange(e);
                      }}
                      txtRef={yearBuiltMinDateRef}
                      onBlur={() => {
                        handleFormValues({ focusOut: true });
                      }}
                    />
                  </li>

                  <li className="text-center">
                    <span className="middle-separator">-</span>
                  </li>
                  <li className="position-relative">
                    <TextBox
                      type="number"
                      minValue={YEAR_BUILT_MIN}
                      maxValue={YEAR_BUILT_MAX}
                      errorMessage={yearBuiltMaxError}
                      value={yrBuiltMax}
                      disabled={isDisableYearBuildMax}
                      errorClass="error-validation-msg"
                      placeholder="Max year"
                      className="form-control"
                      onChange={(e) => {
                        handleYearBuiltMaxchange(e);
                      }}
                      txtRef={yearBuiltMaxDateRef}
                      onBlur={() => {
                        handleFormValues({ focusOut: true });
                      }}
                    />
                  </li>
                </ul>
              </div>
            )}

            <div className="col-md-12">
              <label className="radio-wrap mb-3">
                <input
                  type="radio"
                  name="radio_yearBuilt"
                  value=""
                  onClick={(e) => handleYearBuiltSelection(NEW_CONSTRUCTION)}
                  checked={
                    yearBuiltOption && yearBuiltOption == NEW_CONSTRUCTION
                  }
                  onChange={() => {}} //Avoid console warning
                />
                <span>
                  <div className="timing-wrap-info">
                    <h2 className="wanted-info-list">New construction only</h2>
                  </div>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="range-values-wrap">
        <label className="form-label mb-12 wanted-info-label">Additional Requirements</label>
        <div className="row">
          <div className="wantad-timing-wrap pb-0">
            <div className="col-md-12">
              <label className="check-wrap mb-3">
                <input
                  type="checkbox"
                  name="chk-mh"
                  checked={adaAccess ? true : false}
                  onChange={(e) => handleCheckBoxItemClick(e)}
                  onBlur={() => {
                    handleFormValues({ focusOut: true });
                  }}
                />
                <span>
                  <div className="timing-wrap-info">
                    <h2 className="pb-0 wanted-info-list">ADA Accessible</h2>
                  </div>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default WantAdMustHaves;
